import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container, Overline, useCollection } from "@/components/common";
import PageHero from "@/components/PageHero";
import { getContentItem, getContent } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Warning } from "@phosphor-icons/react";

const Block = ({ title, children }) => (
  <div className="border-t border-slate-200 py-8 first:border-t-0 first:pt-0">
    <Overline className="text-gold-dark">{title}</Overline>
    <div className="mt-3 text-base leading-relaxed text-softgrey-600">{children}</div>
  </div>
);

export default function ClinicalDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { data: pubs } = useCollection(() => getContent("publications", { limit: 4 }), []);

  useEffect(() => {
    setItem(null); setNotFound(false);
    getContentItem("clinical_expertise", slug).then(setItem).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="pt-40 pb-24 text-center"><p className="text-softgrey-600">Topic not found.</p><Link to="/clinical-expertise" className="mt-4 inline-block text-gold-dark">← Back to Clinical Expertise</Link></div>;
  if (!item) return <div className="pt-40 pb-24 text-center text-softgrey-600">Loading…</div>;

  return (
    <div data-testid="clinical-detail-page">
      <PageHero label="Clinical Expertise" title={item.title} intro={item.summary}
        image="https://images.unsplash.com/photo-1496865534669-25ec2a3a0fd3?crop=entropy&cs=srgb&fm=jpg" />
      <section className="bg-white py-16">
        <Container>
          <div className="grid gap-14 md:grid-cols-12">
            <div className="md:col-span-8">
              <Block title="Overview">{item.overview}</Block>
              {item.what_is && <Block title="What it is">{item.what_is}</Block>}
              {item.diagnosis && <Block title="Diagnosis">{item.diagnosis}</Block>}
              {item.treatment && <Block title="Treatment Approaches">{item.treatment}</Block>}
              {item.when_to_seek && <Block title="When to Seek Specialist Advice">{item.when_to_seek}</Block>}
              {item.faqs?.length > 0 && (
                <div className="border-t border-slate-200 py-8">
                  <Overline className="text-gold-dark">Frequently Asked Questions</Overline>
                  <Accordion type="single" collapsible className="mt-4">
                    {item.faqs.map((f, i) => (
                      <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                        <AccordionTrigger className="text-left font-serif text-lg text-navy">{f.q}</AccordionTrigger>
                        <AccordionContent className="text-sm text-softgrey-600">{f.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>
            <aside className="md:col-span-4">
              <div className="sticky top-28 space-y-6">
                <div className="rounded-lg border border-gold/40 bg-gold/5 p-5">
                  <div className="flex items-center gap-2 text-navy"><Warning size={18} className="text-gold-dark" /><span className="text-sm font-semibold">Important</span></div>
                  <p className="mt-2 text-xs leading-relaxed text-navy/70">This content is educational and does not replace an individual medical consultation.</p>
                  <Link to="/consultation"><button className="mt-4 w-full rounded-md bg-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-700">Request a Consultation</button></Link>
                </div>
                <div className="rounded-lg border border-slate-200 p-5">
                  <Overline className="text-gold-dark">Related Research</Overline>
                  <ul className="mt-3 space-y-3">
                    {pubs.slice(0, 3).map((p) => (
                      <li key={p.id}><a href={p.url} target="_blank" rel="noopener noreferrer" className="text-sm text-navy/80 hover:text-gold-dark">{p.title} <span className="text-xs text-softgrey-400">({p.year})</span></a></li>
                    ))}
                  </ul>
                  <Link to="/research" className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-gold-dark">All publications <ArrowRight size={12} /></Link>
                </div>
              </div>
            </aside>
          </div>
        </Container>
      </section>
    </div>
  );
}
